import { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { 
    collection, 
    doc, 
    setDoc, 
    getDocs, 
    query, 
    where,
    serverTimestamp 
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Template component props
interface WhatsappBusinessCloudTemplatesProps {
    accountId: string;
    accountName: string;
    accessToken: string;
    onClose: () => void;
}

// Template types based on WhatsApp Business API
type TemplateCategory = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
type TemplateStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
type TemplateType = 'TEXT' | 'MEDIA' | 'INTERACTIVE';
type ComponentType = 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';

// Template component interface
interface TemplateComponent {
    type: ComponentType;
    text?: string;
    format?: string;
    example?: {
        header_text?: string[];
        body_text?: string[][];
        header_handle?: string[];
    };
    uploadedFile?: File;
    uploadedFileUrl?: string;
    isUploading?: boolean;
    uploadError?: string;
    uploadedHandle?: string;
}

// Template interface
interface WhatsappTemplate {
    id?: string;
    name: string;
    category: TemplateCategory;
    language: string;
    status?: TemplateStatus;
    components: TemplateComponent[];
    createdAt?: any;
    updatedAt?: any;
}

const WhatsappBusinessCloudTemplates = ({ 
    accountId, 
    accountName, 
    accessToken, 
    onClose 
}: WhatsappBusinessCloudTemplatesProps) => {
    // State for template management
    const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'view' | 'create'>('view');
    
    // New template form state
    const [newTemplate, setNewTemplate] = useState<WhatsappTemplate>({
        name: '',
        category: 'UTILITY',
        language: 'en_US',
        components: [
            {
                type: 'HEADER',
                format: 'TEXT',
                text: ''
            },
            {
                type: 'BODY',
                text: ''
            }
        ]
    });
    
    // State for delete template confirmation modal
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
    const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
    const [isDeletingTemplate, setIsDeletingTemplate] = useState<boolean>(false);
    
    // Fetch templates on component mount
    useEffect(() => {
        fetchTemplates();
    }, [accountId]);
    
    // Function to fetch templates from the WhatsApp Business API
    const fetchTemplates = async () => {
        try {
            setIsLoading(true);
            setError(null);
            
            // Fetch templates from Facebook Graph API
            const response = await fetch(
                `https://graph.facebook.com/v22.0/${accountId}/message_templates?access_token=${accessToken}`
            );
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error?.message || 'Failed to fetch templates');
            }
            
            const data = await response.json();
            
            // Map API response to our template interface
            const fetchedTemplates: WhatsappTemplate[] = data.data.map((template: any) => ({
                id: template.id,
                name: template.name,
                category: template.category,
                language: template.language,
                status: template.status,
                components: template.components || [],
                createdAt: template.created_time ? new Date(template.created_time) : null,
                updatedAt: template.updated_time ? new Date(template.updated_time) : null
            }));
            
            setTemplates(fetchedTemplates);
        } catch (error) {
            console.error('Error fetching templates:', error);
            setError(error instanceof Error ? error.message : 'Failed to fetch templates');
        } finally {
            setIsLoading(false);
        }
    };
    
    // Function to handle file upload for media components
    const handleFileUpload = async (index: number, file: File | null) => {
        if (!file) return;
        
        // Update component with file info
        const updatedComponents = [...newTemplate.components];
        updatedComponents[index] = {
            ...updatedComponents[index],
            uploadedFile: file,
            uploadedFileUrl: URL.createObjectURL(file),
            isUploading: true,
            uploadError: undefined,
            uploadedHandle: undefined
        };
        
        setNewTemplate({
            ...newTemplate,
            components: updatedComponents
        });
        
        try {
            console.log(`Uploading ${file.name} for WhatsApp template`);
            
            // Convert file to base64 for sending to Cloud Function
            const fileReader = new FileReader();
            
            // Create a promise to handle the FileReader async operation
            const readFileAsBase64 = new Promise<string>((resolve, reject) => {
                fileReader.onload = () => {
                    // The result is a string with format "data:mimetype;base64,base64data"
                    // We need to extract just the base64 data part
                    const base64WithPrefix = fileReader.result as string;
                    const base64Data = base64WithPrefix.split(',')[1];
                    resolve(base64Data);
                };
                fileReader.onerror = () => {
                    reject(new Error('Failed to read file'));
                };
            });
            
            fileReader.readAsDataURL(file);
            const base64Data = await readFileAsBase64;
            
            // Call the Firebase Cloud Function for file upload
            const functions = getFunctions();
            const uploadWhatsAppTemplateMedia = httpsCallable(functions, 'uploadWhatsAppTemplateMedia');
            
            // The Facebook App ID used in your WhatsApp integration
            const appId = '1201840001461838'; // Your Facebook App ID from WhatsappBusinessCloud.tsx
            
            const uploadResponse = await uploadWhatsAppTemplateMedia({
                accessToken,
                mediaData: base64Data,
                mimeType: file.type,
                filename: file.name,
                appId
            });
            
            console.log('File upload response:', uploadResponse);
            
            if (!uploadResponse.data || !(uploadResponse.data as any).success) {
                throw new Error('Failed to upload file');
            }
            
            // Extract the file handle from the response
            const fileHandle = (uploadResponse.data as any).handle;
            
            // Update component with the file handle
            const updatedComponentsAfterUpload = [...newTemplate.components];
            updatedComponentsAfterUpload[index] = {
                ...updatedComponentsAfterUpload[index],
                isUploading: false,
                uploadedHandle: fileHandle
            };
            
            setNewTemplate({
                ...newTemplate,
                components: updatedComponentsAfterUpload
            });
            
            console.log(`File uploaded successfully. Handle: ${fileHandle}`);
        } catch (error) {
            console.error('Error uploading file:', error);
            
            // Update component with error
            const updatedComponentsWithError = [...newTemplate.components];
            updatedComponentsWithError[index] = {
                ...updatedComponentsWithError[index],
                isUploading: false,
                uploadError: error instanceof Error ? error.message : 'Failed to upload file'
            };
            
            setNewTemplate({
                ...newTemplate,
                components: updatedComponentsWithError
            });
        }
    };
    
    // Function to create a new template
    const createTemplate = async () => {
        try {
            setIsLoading(true);
            setError(null);
            
            // Validate template data
            if (!newTemplate.name) {
                throw new Error('Template name is required');
            }
            
            if (!newTemplate.components.find(c => c.type === 'BODY')?.text) {
                throw new Error('Template body is required');
            }
            
            // Check if any media component doesn't have an uploaded file
            const mediaHeaderComponent = newTemplate.components.find(c => 
                c.type === 'HEADER' && 
                ['IMAGE', 'VIDEO'].includes(c.format || '') && 
                !c.uploadedHandle
            );
            
            if (mediaHeaderComponent) {
                throw new Error(`Please upload a file for the ${mediaHeaderComponent.format} header`);
            }
            
            // Prepare components for API call with required examples
            const components = newTemplate.components.filter(component => {
                // Only include components that have content
                if (component.type === 'BODY' || component.type === 'FOOTER') {
                    return component.text && component.text.trim() !== '';
                }
                return true;
            }).map(component => {
                // Create a clean component without unnecessary properties
                let enhancedComponent = {...component};
                
                // Remove properties that shouldn't be sent to the API
                const { 
                    uploadedFile, 
                    uploadedFileUrl, 
                    isUploading, 
                    uploadError, 
                    uploadedHandle, 
                    ...cleanComponent 
                } = enhancedComponent;
                
                enhancedComponent = cleanComponent;
                
                // For media headers (IMAGE, VIDEO), we should not include the text property
                if (component.type === 'HEADER') {
                    if (['IMAGE', 'VIDEO'].includes(component.format || '')) {
                        // Remove text property for media headers as it's not needed and causes 'invalid parameter' errors
                        const { text, ...headerWithoutText } = enhancedComponent;
                        enhancedComponent = headerWithoutText;
                        
                        // Use the uploaded file handle if available
                        if (component.uploadedHandle) {
                            enhancedComponent.example = {
                                header_handle: [component.uploadedHandle]
                            };
                        } 
                        // Fallback to example values if no upload (shouldn't happen due to validation)
                        else {
                            enhancedComponent.example = {};
                            
                            if (component.format === 'IMAGE') {
                                enhancedComponent.example.header_handle = ['https://www.example.com/image.jpg'];
                            } 
                            else if (component.format === 'VIDEO') {
                                enhancedComponent.example.header_handle = ['https://www.example.com/video.mp4'];
                            }
                        }
                    }
                    else if (component.format === 'TEXT' && component.text) {
                        // Extract variables like {{1}} from text
                        const variables = component.text.match(/\{\{\d+\}\}/g) || [];
                        if (variables.length > 0) {
                            enhancedComponent.example = {
                                header_text: ['Sample Header']
                            };
                        }
                    }
                } else if (component.type === 'BODY' && component.text) {
                    // Extract variables like {{1}} from text
                    const variables = component.text.match(/\{\{\d+\}\}/g) || [];
                    if (variables.length > 0) {
                        const exampleValues = variables.map((_, index) => `example${index + 1}`);
                        enhancedComponent.example = {
                            body_text: [exampleValues]
                        };
                    }
                }
                
                return enhancedComponent;
            });
            
            // Use the full language code without splitting (e.g., keep 'en_US' as is)
            // Log the components structure to debug media type issues
            console.log('Template components being sent:', components);
            
            // Log the complete request payload
            const requestPayload = {
                name: newTemplate.name.replace(/\s+/g, '_').toLowerCase(),
                category: newTemplate.category,
                language: newTemplate.language, // Use full language code without splitting
                allow_category_change: true,
                components
            };
            console.log('Full template creation request payload:', requestPayload);
            
            // Create template via Facebook Graph API
            const response = await fetch(
                `https://graph.facebook.com/v22.0/${accountId}/message_templates`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    },
                    body: JSON.stringify(requestPayload)
                }
            );
            
            // Log the raw response
            console.log('Template creation response status:', response.status, response.statusText);
            
            const responseData = await response.json();
            console.log('Template creation response data:', responseData);
            
            if (!response.ok) {
                console.error('Template creation failed with error:', responseData);
                throw new Error(responseData.error?.message || 'Failed to create template');
            }
            
            const data = responseData;
            
            // Save template to Firestore for tracking
            // await setDoc(
            //     doc(db, 'whatsappBusinessCloudAccounts', accountId, 'templates', data.id),
            //     {
            //         ...newTemplate,
            //         id: data.id,
            //         createdAt: serverTimestamp(),
            //         updatedAt: serverTimestamp()
            //     }
            // );
            
            // Reset form and refresh templates
            setNewTemplate({
                name: '',
                category: 'UTILITY',
                language: 'en_US',
                components: [
                    {
                        type: 'HEADER',
                        format: 'TEXT',
                        text: ''
                    },
                    {
                        type: 'BODY',
                        text: ''
                    }
                ]
            });
            
            // Refresh templates list
            await fetchTemplates();
            
            // Switch to view tab
            setActiveTab('view');
        } catch (error) {
            console.error('Error creating template:', error);
            setError(error instanceof Error ? error.message : 'Failed to create template');
        } finally {
            setIsLoading(false);
        }
    };
    
    // Function to delete a template
    const deleteTemplate = async () => {
        try {
            setIsDeletingTemplate(true);
            
            // Delete template via Facebook Graph API using name as query parameter
            console.log('Deleting template:', templateToDelete);
            console.log('Access token:', accessToken);
            console.log('Account ID:', accountId);
            
            const response = await fetch(
                `https://graph.facebook.com/v22.0/${accountId}/message_templates?name=${templateToDelete}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error?.message || 'Failed to delete template');
            }
            
            console.log('Template deleted successfully');
            
            // Refresh templates list
            await fetchTemplates();
        } catch (error) {
            console.error('Error deleting template:', error);
            setError(error instanceof Error ? error.message : 'Failed to delete template');
        } finally {
            setIsDeletingTemplate(false);
            setShowDeleteConfirmation(false);
        }
    };
    
    // Handle input changes for new template form
    const handleInputChange = (field: string, value: string) => {
        setNewTemplate({
            ...newTemplate,
            [field]: value
        });
    };
    
    // Handle component changes
    const handleComponentChange = (index: number, field: string, value: string) => {
        const updatedComponents = [...newTemplate.components];
        updatedComponents[index] = {
            ...updatedComponents[index],
            [field]: value
        };
        
        setNewTemplate({
            ...newTemplate,
            components: updatedComponents
        });
    };
    
    // Add a component to the template
    const addComponent = (type: ComponentType) => {
        const newComponent: TemplateComponent = { type };
        
        if (type === 'BODY' || type === 'FOOTER') {
            newComponent.text = '';
        } else if (type === 'HEADER') {
            newComponent.format = 'TEXT'; // Default to TEXT, user can change to media types
            newComponent.text = '';
        }
        
        setNewTemplate({
            ...newTemplate,
            components: [...newTemplate.components, newComponent]
        });
    };
    
    // Get status badge color based on template status
    const getStatusColor = (status?: TemplateStatus) => {
        switch (status) {
            case 'APPROVED':
                return 'bg-green-100 text-green-800';
            case 'REJECTED':
                return 'bg-red-100 text-red-800';
            case 'PENDING':
            default:
                return 'bg-yellow-100 text-yellow-800';
        }
    };
    
    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-5 flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                        <div className="bg-white p-2 rounded-full">
                            <svg className="h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.645.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0.5 6.6c-3.17 0-5.75 2.58-5.75 5.75 0 3.17 2.58 5.75 5.75 5.75 3.17 0 5.75-2.58 5.75-5.75 0-3.17-2.58-5.75-5.75-5.75zm3.5 5.75c0-1.93-1.57-3.5-3.5-3.5s-3.5 1.57-3.5 3.5 1.57 3.5 3.5 3.5 3.5-1.57 3.5-3.5z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-white tracking-tight">
                            WhatsApp Templates - {accountName}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-blue-800 focus:outline-none p-2 rounded-full transition-colors duration-200"
                    >
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                
                {/* Tabs */}
                <div className="px-6 pt-4 pb-2 bg-gray-50 border-b border-gray-200">
                    <div className="flex space-x-6">
                        <button
                            className={`px-4 py-3 font-semibold text-sm rounded-t-md transition-colors duration-200 relative ${activeTab === 'view' ? 'text-blue-600 hover:text-blue-800' : 'text-gray-600 hover:text-gray-800'}`}
                            onClick={() => setActiveTab('view')}
                        >
                            <div className="flex items-center space-x-2">
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span>View Templates</span>
                            </div>
                            {activeTab === 'view' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>}
                        </button>
                        <button
                            className={`px-4 py-3 font-semibold text-sm rounded-t-md transition-colors duration-200 relative ${activeTab === 'create' ? 'text-blue-600 hover:text-blue-800' : 'text-gray-600 hover:text-gray-800'}`}
                            onClick={() => setActiveTab('create')}
                        >
                            <div className="flex items-center space-x-2">
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                <span>Create New Template</span>
                            </div>
                            {activeTab === 'create' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>}
                        </button>
                    </div>
                </div>
                
                {/* Content */}
                <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 8rem)' }}>
                    {error && (
                        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                            {error}
                        </div>
                    )}
                    
                    {isLoading ? (
                        <div className="flex justify-center items-center py-12">
                            <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <span className="ml-3 text-gray-600">Loading templates...</span>
                        </div>
                    ) : activeTab === 'view' ? (
                        /* View Templates Tab */
                        <div>
                            {templates.length > 0 ? (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {templates.map((template) => (
                                        <div key={template.id} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                                            {/* Card Header */}
                                            <div className="bg-gray-50 border-b border-gray-200 px-5 py-4 flex justify-between items-center">
                                                <div className="flex items-center space-x-3">
                                                    <div className="bg-blue-100 rounded-full p-2">
                                                        <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 8h10M7 12h4m1-8l-4-4H5a2 2 0 01-2-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                        </svg>
                                                    </div>
                                                    <h3 className="font-semibold text-gray-900 tracking-tight">{template.name}</h3>
                                                </div>
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(template.status)}`}>
                                                    {template.status || 'PENDING'}
                                                </span>
                                            </div>
                                            
                                            {/* Card Content */}
                                            <div className="p-5">
                                                <div className="flex flex-wrap gap-3 mb-4">
                                                    <div className={`px-3 py-1.5 rounded-full text-xs font-medium ${template.category === 'UTILITY' ? 'bg-blue-100 text-blue-800' : template.category === 'MARKETING' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                        <span className="inline-flex items-center">
                                                            {template.category === 'UTILITY' && (
                                                                <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                                                </svg>
                                                            )}
                                                            {template.category === 'MARKETING' && (
                                                                <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                                                                </svg>
                                                            )}
                                                            {template.category === 'AUTHENTICATION' && (
                                                                <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                                </svg>
                                                            )}
                                                            {template.category}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                                        <span className="inline-block w-3 h-3 rounded-full bg-indigo-600 mr-1.5 flex-shrink-0"></span>
                                                        {template.language}
                                                    </div>
                                                </div>
                                                
                                                {/* Template Preview */}
                                                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-5 border border-gray-200 mt-4 shadow-sm">
                                                    <div className="flex items-center mb-4">
                                                        <svg className="h-5 w-5 text-purple-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <h4 className="text-sm font-semibold text-gray-700 tracking-wide">Template Preview</h4>
                                                    </div>
                                                    <div className="space-y-3 max-w-xs mx-auto bg-white rounded-lg p-4 border border-gray-300 shadow-md relative overflow-hidden">
                                                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 via-blue-500 to-purple-500"></div>
                                                        {template.components?.map((component, index) => {
                                                            if (component.type === 'HEADER') {
                                                                if (component.format === 'TEXT' && component.text) {
                                                                    return (
                                                                        <div key={index} className="font-medium text-gray-900 pb-2 border-b border-gray-100">{component.text}</div>
                                                                    );
                                                                } else if (component.format === 'IMAGE') {
                                                                    return (
                                                                        <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 h-40 rounded-lg flex flex-col items-center justify-center border border-blue-100 overflow-hidden">
                                                                            <svg className="h-12 w-12 text-blue-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h8a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                            </svg>
                                                                            <span className="text-xs text-blue-500 font-medium">Image Preview</span>
                                                                        </div>
                                                                    );
                                                                } else if (component.format === 'VIDEO') {
                                                                    return (
                                                                        <div key={index} className="bg-gradient-to-r from-purple-50 to-pink-50 h-40 rounded-lg flex flex-col items-center justify-center border border-purple-100 overflow-hidden">
                                                                            <svg className="h-12 w-12 text-purple-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                                            </svg>
                                                                            <span className="text-xs text-purple-500 font-medium">Video Preview</span>
                                                                        </div>
                                                                    );
                                                                }
                                                            } else if (component.type === 'BODY' && component.text) {
                                                                return (
                                                                    <div key={index} className="text-sm text-gray-700 whitespace-pre-line py-3">{component.text}</div>
                                                                );
                                                            } else if (component.type === 'FOOTER' && component.text) {
                                                                return (
                                                                    <div key={index} className="text-xs text-gray-500 italic pt-2 border-t border-gray-100">{component.text}</div>
                                                                );
                                                            }
                                                            return null;
                                                        })}
                                                    </div>
                                                </div>
                                                
                                                {template.createdAt && (
                                                    <div className="mt-3 text-xs text-gray-500">
                                                        Created: {template.createdAt.toLocaleString?.() || 'Unknown'}
                                                    </div>
                                                )}
                                                
                                                {/* Delete Template Button */}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setTemplateToDelete(template.name || null);
                                                        setShowDeleteConfirmation(true);
                                                    }}
                                                    className="mt-4 inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                >
                                                    <svg className="h-4 w-4 text-red-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                    Delete Template
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                    </svg>
                                    <p className="mt-2 text-gray-600">No templates found. Create your first template!</p>
                                    <button
                                        onClick={() => setActiveTab('create')}
                                        className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        Create Template
                                    </button>
                                </div>
                            )}
                            
                            {templates.length > 0 && (
                                <div className="mt-6 text-right">
                                    <button
                                        onClick={() => fetchTemplates()}
                                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        <svg className="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Refresh
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Create Template Tab */
                        <div className="relative">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Left column (scrollable content) */}
                                <div className="lg:col-span-2 space-y-6 overflow-y-auto pr-2 max-h-[calc(100vh-220px)]">

                                {/* Basic Information */}
                                <div className="bg-gradient-to-b from-purple-50 to-white p-6 rounded-xl shadow-sm border border-purple-100">
                                    <div className="flex items-center mb-5">
                                        <div className="bg-purple-100 p-2 rounded-full mr-3">
                                            <svg className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                                            <div className="flex items-center mb-2">
                                                <svg className="h-4 w-4 text-purple-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                </svg>
                                                <label htmlFor="template-name" className="block text-sm font-medium text-gray-800">
                                                    Template Name <span className="text-red-500">*</span>
                                                </label>
                                            </div>
                                            <input
                                                type="text"
                                                id="template-name"
                                                value={newTemplate.name}
                                                onChange={(e) => handleInputChange('name', e.target.value)}
                                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base py-3 px-4 transition-all duration-200 hover:border-blue-300"
                                                placeholder="welcome_message"
                                            />
                                            <div className="mt-2 flex items-start">
                                                <svg className="h-4 w-4 text-blue-500 mr-1 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <p className="text-xs text-gray-500">
                                                    Use lowercase and underscores. No spaces allowed.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                                            <div className="flex items-center mb-2">
                                                <svg className="h-4 w-4 text-purple-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                </svg>
                                                <label htmlFor="template-category" className="block text-sm font-medium text-gray-800">
                                                    Category <span className="text-red-500">*</span>
                                                </label>
                                            </div>
                                            <div className="mt-2">
                                                <div className="flex flex-wrap gap-3 mt-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleInputChange('category', 'UTILITY' as TemplateCategory)}
                                                        className={`inline-flex items-center px-4 py-2.5 rounded-lg text-sm font-medium ${newTemplate.category === 'UTILITY' ? 'bg-blue-100 text-blue-800 border-2 border-blue-300 shadow-sm' : 'bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200'}`}
                                                    >
                                                        <svg className="mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                                        </svg>
                                                        UTILITY
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleInputChange('category', 'MARKETING' as TemplateCategory)}
                                                        className={`inline-flex items-center px-4 py-2.5 rounded-lg text-sm font-medium ${newTemplate.category === 'MARKETING' ? 'bg-green-100 text-green-800 border-2 border-green-300 shadow-sm' : 'bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200'}`}
                                                    >
                                                        <svg className="mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                                                        </svg>
                                                        MARKETING
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleInputChange('category', 'AUTHENTICATION' as TemplateCategory)}
                                                        className={`inline-flex items-center px-4 py-2.5 rounded-lg text-sm font-medium ${newTemplate.category === 'AUTHENTICATION' ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-300 shadow-sm' : 'bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200'}`}
                                                    >
                                                        <svg className="mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                        </svg>
                                                        AUTHENTICATION
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 sm:col-span-2">
                                            <div className="flex items-center mb-2">
                                                <svg className="h-4 w-4 text-purple-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                                </svg>
                                                <label htmlFor="template-language" className="block text-sm font-medium text-gray-800">
                                                    Language <span className="text-red-500">*</span>
                                                </label>
                                            </div>
                                            <div className="relative">
                                                <select
                                                    id="template-language"
                                                    value={newTemplate.language}
                                                    onChange={(e) => handleInputChange('language', e.target.value)}
                                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base py-3 appearance-none pl-2 pr-10 transition-all duration-200 hover:border-blue-300"
                                                >
                                                    <option value="en_US">English (US)</option>
                                                    <option value="en_GB">English (UK)</option>
                                                    <option value="es_ES">Spanish</option>
                                                    <option value="pt_BR">Portuguese (Brazil)</option>
                                                    <option value="fr_FR">French</option>
                                                    <option value="de_DE">German</option>
                                                    <option value="it_IT">Italian</option>
                                                    <option value="hi_IN">Hindi</option>
                                                    <option value="ar_AR">Arabic</option>
                                                </select>
                                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="flex items-center mt-2">
                                                <div className="h-6 w-6 rounded-full bg-blue-500 flex items-center justify-center text-white mr-2 flex-shrink-0">
                                                    <span className="text-xs font-bold">{ newTemplate.language.split('_')[0].toUpperCase() }</span>
                                                </div>
                                                <p className="text-xs text-gray-500">
                                                    {newTemplate.language === 'en_US' ? 'English (United States)' : 
                                                     newTemplate.language === 'en_GB' ? 'English (United Kingdom)' :
                                                     newTemplate.language === 'es_ES' ? 'Spanish (Spain)' :
                                                     newTemplate.language === 'pt_BR' ? 'Portuguese (Brazil)' :
                                                     newTemplate.language === 'fr_FR' ? 'French (France)' :
                                                     newTemplate.language === 'de_DE' ? 'German (Germany)' :
                                                     newTemplate.language === 'it_IT' ? 'Italian (Italy)' :
                                                     newTemplate.language === 'hi_IN' ? 'Hindi (India)' :
                                                     newTemplate.language === 'ar_AR' ? 'Arabic' : newTemplate.language}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Template Components */}
                                <div className="bg-gradient-to-b from-gray-50 to-white p-6 rounded-xl shadow-sm border border-gray-200">
                                    <div className="flex items-center mb-4">
                                        <div className="bg-blue-100 p-2 rounded-full mr-3">
                                            <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-lg font-semibold text-gray-900">Template Components</h3>
                                    </div>
                                    
                                    <div className="space-y-5">
                                        {newTemplate.components.map((component, index) => (
                                            <div key={index} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm transition-all hover:shadow-md">
                                                <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-100">
                                                    <div className="flex items-center">
                                                        {component.type === 'HEADER' && (
                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mr-2">
                                                                HEADER
                                                            </span>
                                                        )}
                                                        {component.type === 'BODY' && (
                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                                                                BODY
                                                            </span>
                                                        )}
                                                        {component.type === 'FOOTER' && (
                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                                                                FOOTER
                                                            </span>
                                                        )}
                                                        
                                                        {component.type === 'HEADER' && component.format && (
                                                            <span className="text-xs text-gray-500 ml-2">
                                                                Format: {component.format}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                {component.type === 'HEADER' && (
                                                    <div className="mb-4">
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Format
                                                        </label>
                                                        <div className="flex flex-wrap gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleComponentChange(index, 'format', 'TEXT')}
                                                                className={`inline-flex items-center px-3 py-2 border ${component.format === 'TEXT' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700'} rounded-md text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                                                            >
                                                                <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h.01M3 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                                </svg>
                                                                Text
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleComponentChange(index, 'format', 'IMAGE')}
                                                                className={`inline-flex items-center px-3 py-2 border ${component.format === 'IMAGE' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700'} rounded-md text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                                                            >
                                                                <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h8a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                </svg>
                                                                Image
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleComponentChange(index, 'format', 'VIDEO')}
                                                                className={`inline-flex items-center px-3 py-2 border ${component.format === 'VIDEO' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700'} rounded-md text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                                                            >
                                                                <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                                </svg>
                                                                Video
                                                            </button>
                                                        </div>
                                                        
                                                        {/* Add explanatory information for each media format */}
                                                        {component.format !== 'TEXT' && (
                                                            <div className="mt-3 bg-blue-50 border border-blue-100 rounded-md p-3">
                                                                <div className="flex items-center mb-1">
                                                                    <svg className="h-4 w-4 text-blue-500 mr-1.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                    </svg>
                                                                    <span className="text-sm font-medium text-blue-700">
                                                                        {component.format} Template Information
                                                                    </span>
                                                                </div>
                                                                <div className="text-xs text-blue-600 ml-5.5">
                                                                    {component.format === 'IMAGE' && (
                                                                        <>
                                                                            • Media will be uploaded after template approval<br />
                                                                            • Recommended size: 800x800px<br />
                                                                            • Supported formats: JPG, PNG<br />
                                                                            • Max file size: 5MB
                                                                        </>
                                                                    )}
                                                                    {component.format === 'VIDEO' && (
                                                                        <>
                                                                            • Media will be uploaded after template approval<br />
                                                                            • Recommended length: 15-30 seconds<br />
                                                                            • Supported format: MP4<br />
                                                                            • Max file size: 16MB
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                
                                                {(component.type === 'BODY' || component.type === 'FOOTER' || (component.type === 'HEADER' && component.format === 'TEXT')) && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Text {component.type === 'BODY' && <span className="text-red-500">*</span>}
                                                        </label>
                                                        <textarea
                                                            value={component.text || ''}
                                                            onChange={(e) => handleComponentChange(index, 'text', e.target.value)}
                                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2"
                                                            rows={component.type === 'BODY' ? 4 : 2}
                                                            placeholder={component.type === 'BODY' ? 'Hello {{1}}, your appointment is confirmed for {{2}}.' : 
                                                                     component.type === 'HEADER' ? 'Order Confirmation' : 'Thank you for your business'}
                                                        />
                                                        {component.type === 'BODY' && (
                                                            <p className="mt-1 text-xs text-gray-500">
                                                                Use {'{{'} 1 {'}}}'}, {'{{'} 2 {'}}'} etc. for variables that will be replaced when sending messages.
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                                
                                                {(component.type === 'HEADER' && component.format !== 'TEXT') && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Upload Media
                                                        </label>
                                                        <input
                                                            type="file"
                                                            accept={component.format === 'IMAGE' ? 'image/jpeg, image/png' : component.format === 'VIDEO' ? 'video/mp4' : ''}
                                                            onChange={(e) => handleFileUpload(index, e.target.files ? e.target.files[0] : null)}
                                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base py-3 px-4 transition-all duration-200 hover:border-blue-300"
                                                        />
                                                        {component.uploadedFile && (
                                                            <div className="mt-2 flex items-center">
                                                                <div className="h-6 w-6 rounded-full bg-green-500 flex items-center justify-center text-white mr-2 flex-shrink-0">
                                                                    <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                    </svg>
                                                                </div>
                                                                <p className="text-xs text-gray-500">
                                                                    {component.uploadedFile?.name}
                                                                </p>
                                                            </div>
                                                        )}
                                                        {component.uploadError && (
                                                            <div className="mt-2 text-xs text-red-500">
                                                                {component.uploadError}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {/* Add Component Buttons */}
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {!newTemplate.components.find(c => c.type === 'FOOTER') && (
                                            <button
                                                type="button"
                                                onClick={() => addComponent('FOOTER')}
                                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500"
                                            >
                                                Add Footer
                                            </button>
                                        )}
                                    </div>
                                </div>
                                
                                </div>
                                
                                {/* Right column (fixed preview) */}
                                <div className="lg:col-span-1">
                                    <div className="sticky top-6 pb-6">
                                        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden max-w-xs mx-auto">
                                            {/* Phone-like header */}
                                            <div className="bg-gradient-to-r from-green-600 to-teal-600 p-3 text-white relative">
                                                <div className="flex items-center">
                                                    <svg className="h-5 w-5 text-white mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                                    </svg>
                                                    <span className="font-medium">WhatsApp Business</span>
                                                </div>
                                                <div className="text-xs mt-2 flex items-center">
                                                    <div className="h-6 w-6 rounded-full bg-gray-100 flex-shrink-0 mr-2 flex items-center justify-center">
                                                        <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <div className="font-medium">Message Preview</div>
                                                        <div className="opacity-70 text-xs">Template: {newTemplate.name || 'Unnamed'}</div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Message body with chat-like background */}
                                            <div className="bg-[#f0f0f0] h-[calc(70vh-220px)] p-3 overflow-y-auto" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z\' fill=\'%23e2e8f0\' fill-opacity=\'0.2\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")', backgroundSize: '80px 80px', backgroundRepeat: 'repeat' }}>
                                                <div className="flex flex-col items-end">
                                                    <div className="bg-white rounded-lg p-3 shadow-sm max-w-[85%] relative mb-3 overflow-hidden">
                                                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 via-blue-500 to-purple-500"></div>
                                                        <div className="space-y-3">
                                                            {newTemplate.components.map((component, index) => {
                                                                if (component.type === 'HEADER') {
                                                                    if (component.format === 'TEXT' && component.text) {
                                                                        return (
                                                                            <div key={index} className="font-bold text-gray-900 pb-2 border-b border-gray-100">
                                                                                {component.text}
                                                                            </div>
                                                                        );
                                                                    } else if (component.format === 'IMAGE') {
                                                                        return (
                                                                            <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 h-36 rounded-lg flex flex-col items-center justify-center border border-blue-100 overflow-hidden">
                                                                                <svg className="h-10 w-10 text-blue-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h8a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                                </svg>
                                                                                <span className="text-xs text-blue-500 font-medium">
                                                                                    {component.uploadedFile ? 'Image Uploaded' : 'Image Preview'}
                                                                                </span>
                                                                                {component.isUploading && (
                                                                                    <div className="mt-1 text-xs text-blue-500">Uploading...</div>
                                                                                )}
                                                                                {component.uploadError && (
                                                                                    <div className="mt-1 text-xs text-red-500">{component.uploadError}</div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    } else if (component.format === 'VIDEO') {
                                                                        return (
                                                                            <div key={index} className="bg-gradient-to-r from-purple-50 to-pink-50 h-36 rounded-lg flex flex-col items-center justify-center border border-purple-100 overflow-hidden relative">
                                                                                <svg className="h-10 w-10 text-purple-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                                                </svg>
                                                                                <span className="text-xs text-purple-500 font-medium">
                                                                                    {component.uploadedFile ? 'Video Uploaded' : 'Video Preview'}
                                                                                </span>
                                                                                {component.isUploading && (
                                                                                    <div className="mt-1 text-xs text-purple-500">Uploading...</div>
                                                                                )}
                                                                                {component.uploadError && (
                                                                                    <div className="mt-1 text-xs text-red-500">{component.uploadError}</div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    }
                                                                } else if (component.type === 'BODY' && component.text) {
                                                                    return (
                                                                        <div key={index} className="text-sm text-gray-800 whitespace-pre-line py-3">{component.text}</div>
                                                                    );
                                                                } else if (component.type === 'FOOTER' && component.text) {
                                                                    return (
                                                                        <div key={index} className="text-xs text-gray-500 italic pt-2 border-t border-gray-100">{component.text}</div>
                                                                    );
                                                                }
                                                                return null;
                                                            })}
                                                        </div>
                                                        <div className="flex justify-end items-center mt-1 space-x-1">
                                                            <span className="text-xs text-gray-500">{new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                                            <svg className="h-3.5 w-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Phone-like footer with input bar */}
                                            <div className="bg-gray-100 p-2 flex items-center border-t border-gray-200">
                                                <div className="bg-white rounded-full flex-1 py-2 px-3 text-gray-400 text-sm mr-2">
                                                    Preview only
                                                </div>
                                                <div className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                                                    <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="mt-4 bg-white p-3 rounded-lg border border-gray-200 shadow-sm text-xs text-gray-700">
                                            <div className="flex items-center mb-2">
                                                <svg className="h-4 w-4 text-blue-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <span className="font-medium">Live Preview</span>
                                            </div>
                                            <p>Changes to your template will appear in real-time in this WhatsApp message preview.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Form Actions */}
                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('view')}
                                    className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={createTemplate}
                                    disabled={isLoading}
                                    className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isLoading ? 'Creating...' : 'Create Template'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {showDeleteConfirmation && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md h-auto flex flex-col overflow-hidden">
                        <div className="bg-red-100 border-b border-red-200 p-4 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-red-800">Delete Template Confirmation</h3>
                            <button
                                type="button"
                                onClick={() => setShowDeleteConfirmation(false)}
                                className="text-red-800 hover:bg-red-200 focus:outline-none p-2 rounded-full transition-colors duration-200"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="p-6">
                            <p className="text-sm text-gray-600">Are you sure you want to delete the template <span className="font-bold">{templateToDelete}</span>?</p>
                            <p className="text-xs text-gray-500 mt-2">This action cannot be undone.</p>
                        </div>
                        
                        <div className="flex justify-end p-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={() => setShowDeleteConfirmation(false)}
                                className="mr-3 px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={deleteTemplate}
                                disabled={isDeletingTemplate}
                                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isDeletingTemplate ? 'Deleting...' : 'Delete Template'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WhatsappBusinessCloudTemplates;
